import { AuthStorage } from "utils/auth-storage";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  DatePicker,
  Dropdown,
  Select,
  Table,
  Tooltip,
} from "antd";
import { DownOutlined } from "@ant-design/icons";

import Container from "components/container";
import ExportReports from "components/export-reports";
import ExportReportsCSV from "components/export-reports-csv";
import LoadingComponent from "components/loading-component";
import SidebarDistribution from "components/sidebar-distribution/sidebar-distribution";
import Text from "components/text";
import { ReportServices } from "apis/Services/ReportService";
import {
  canAccessCannedReport,
  normalizeReportAccessLevel,
  parseCannedReportsGranularPermission,
} from "../canned-reports/reportPermissions";
import styles from "./SubmittalsLastUpdatedTimeReport.module.css";

const REPORT_TITLE = "Submittals Last Updated Time";
const DISTRIBUTION_REPORT_NAME =
  "canned report - submittals-last-updated-time";
const DATE_FORMAT = "MM-DD-YYYY";
const DEFAULT_FILTERS = {
  startDate: "",
  endDate: "",
  sortBy: "Descending",
};
const DEFAULT_SPEC_SECTION_WIDTH = 340;
const MIN_SPEC_SECTION_WIDTH = 240;
const MAX_SPEC_SECTION_WIDTH = 640;

const INTERNAL_COLUMN_KEYS = new Set([
  "key",
  "entryID",
  "entryId",
  "id",
  "companyID",
  "companyId",
  "projectID",
  "projectId",
  "accountName",
]);

const DEFAULT_COLUMN_KEYS = [
  "specSection",
  "specNumber",
  "specRevision",
  "specTitle",
  "actionDescription",
  "timestamp",
];

const COLUMN_LABELS = {
  specNumber: "Submittal Number",
  submittalNumber: "Submittal Number",
  specRevision: "Submittal Revision",
  submittalRevision: "Submittal Revision",
  specTitle: "Item Name",
  itemName: "Item Name",
  specType: "Submittal Type",
  submittalType: "Submittal Type",
  responsibleContractor: "Responsible Contractor",
  accountName: "Account Name",
  actionDescription: "Action Description",
  specSection: "Spec Section",
  timestamp: "Timestamp",
  lastUpdatedTime: "Last Updated Time",
  lastUpdatedBy: "Last Updated By",
};

const COLUMN_ORDER = [
  "specSection",
  "specNumber",
  "submittalNumber",
  "specRevision",
  "submittalRevision",
  "specTitle",
  "itemName",
  "specType",
  "submittalType",
  "responsibleContractor",
  "actionDescription",
  "timestamp",
  "lastUpdatedBy",
  "lastUpdatedTime",
];

const clampSpecSectionWidth = (width) =>
  Math.min(MAX_SPEC_SECTION_WIDTH, Math.max(MIN_SPEC_SECTION_WIDTH, width));

const ResizableHeaderCell = ({
  resizable,
  width,
  onResize,
  onReset,
  children,
  ...restProps
}) => {
  const cleanupRef = useRef(() => {});

  useEffect(
    () => () => {
      cleanupRef.current();
    },
    [],
  );

  const startResize = (event) => {
    if (!resizable) return;
    event.preventDefault();
    event.stopPropagation();
    cleanupRef.current();

    const startX = event.clientX;
    const startWidth = width;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    const handleMouseMove = (moveEvent) => {
      onResize(
        clampSpecSectionWidth(startWidth + moveEvent.clientX - startX),
      );
    };
    const handleMouseUp = () => cleanupRef.current();
    const cleanup = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      cleanupRef.current = () => {};
    };

    cleanupRef.current = cleanup;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const resizeWithKeyboard = (event) => {
    if (!resizable || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }

    event.preventDefault();
    onResize(
      clampSpecSectionWidth(
        width + (event.key === "ArrowRight" ? 20 : -20),
      ),
    );
  };

  return (
    <th {...restProps} style={{ ...restProps.style, width }}>
      {children}
      {resizable ? (
        <span
          className={styles.resizeHandle}
          role="separator"
          aria-label="Resize Spec Section column"
          aria-orientation="vertical"
          aria-valuemin={MIN_SPEC_SECTION_WIDTH}
          aria-valuemax={MAX_SPEC_SECTION_WIDTH}
          aria-valuenow={width}
          tabIndex={0}
          onMouseDown={startResize}
          onKeyDown={resizeWithKeyboard}
          onDoubleClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onReset();
          }}
        />
      ) : null}
    </th>
  );
};

function humanizeColumnName(value) {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatCellValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

function extractReportRows(response) {
  const candidates = [
    response?.content,
    response?.data,
    response?.submittals,
    response?.content?.data,
    response?.content?.submittals,
    response?.data?.content,
    response?.data?.submittals,
  ];
  const directRows = candidates.find(Array.isArray);

  if (directRows) {
    return directRows;
  }

  const nestedRows = [response?.content, response?.data]
    .filter((candidate) => candidate && typeof candidate === "object")
    .flatMap((candidate) => Object.values(candidate))
    .find(Array.isArray);

  return nestedRows || [];
}

function normalizeReportRows(response) {
  return extractReportRows(response).map((row, index) => ({
    ...row,
    key: String(row?.entryID ?? row?.entryId ?? row?.id ?? index),
  }));
}

function buildReportColumns(rows, specSectionWidth, setSpecSectionWidth) {
  const discoveredKeys = Array.from(
    rows.reduce((keys, row) => {
      Object.keys(row || {}).forEach((key) => {
        if (!INTERNAL_COLUMN_KEYS.has(key)) {
          keys.add(key);
        }
      });
      return keys;
    }, new Set()),
  );
  const sourceKeys = discoveredKeys.length
    ? discoveredKeys
    : DEFAULT_COLUMN_KEYS;

  return sourceKeys
    .sort((left, right) => {
      const leftIndex = COLUMN_ORDER.indexOf(left);
      const rightIndex = COLUMN_ORDER.indexOf(right);
      const normalizedLeftIndex = leftIndex === -1 ? Infinity : leftIndex;
      const normalizedRightIndex = rightIndex === -1 ? Infinity : rightIndex;

      if (normalizedLeftIndex !== normalizedRightIndex) {
        return normalizedLeftIndex - normalizedRightIndex;
      }

      return left.localeCompare(right, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    })
    .map((key) => {
      const isSpecSection = key === "specSection";

      return {
        title: COLUMN_LABELS[key] || humanizeColumnName(key),
        dataIndex: key,
        key,
        width: isSpecSection
          ? specSectionWidth
          : key === "specTitle" || key === "itemName" || key === "title"
            ? 320
            : 180,
        className: isSpecSection ? styles.specSectionColumn : undefined,
        ellipsis: true,
        onHeaderCell: isSpecSection
          ? () => ({
              width: specSectionWidth,
              resizable: true,
              onResize: setSpecSectionWidth,
              onReset: () =>
                setSpecSectionWidth(DEFAULT_SPEC_SECTION_WIDTH),
            })
          : undefined,
        render: formatCellValue,
      };
    });
}

function SubmittalsLastUpdatedTimeReport({ company_id, id, reportName }) {
  const [isAccessLoading, setIsAccessLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [pageAccessLevel, setPageAccessLevel] = useState(0);
  const [cannedReportsGranularPermission, setCannedReportsGranularPermission] =
    useState("");
  const [projectName, setProjectName] = useState("");
  const [projectAddress, setProjectAddress] = useState("");
  const [rows, setRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortBy, setSortBy] = useState(DEFAULT_FILTERS.sortBy);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [specSectionWidth, setSpecSectionWidth] = useState(
    DEFAULT_SPEC_SECTION_WIDTH,
  );

  const allowedReports = useMemo(
    () =>
      parseCannedReportsGranularPermission(
        cannedReportsGranularPermission,
      ),
    [cannedReportsGranularPermission],
  );
  const canViewReport = canAccessCannedReport({
    accessLevel: pageAccessLevel,
    allowedReports,
    reportSlug: reportName,
  });
  const columns = useMemo(
    () => buildReportColumns(rows, specSectionWidth, setSpecSectionWidth),
    [rows, specSectionWidth],
  );
  const draftFilters = useMemo(
    () => ({
      startDate: startDate ? startDate.format(DATE_FORMAT) : "",
      endDate: endDate ? endDate.format(DATE_FORMAT) : "",
      sortBy: sortBy || "",
    }),
    [endDate, sortBy, startDate],
  );
  const hasFilterChanges = useMemo(
    () =>
      draftFilters.startDate !== appliedFilters.startDate ||
      draftFilters.endDate !== appliedFilters.endDate ||
      draftFilters.sortBy !== appliedFilters.sortBy,
    [appliedFilters, draftFilters],
  );
  const hasCompleteDateRange =
    Boolean(draftFilters.startDate) === Boolean(draftFilters.endDate);
  const hasFiltersToClear = useMemo(
    () =>
      Boolean(draftFilters.startDate) ||
      Boolean(draftFilters.endDate) ||
      draftFilters.sortBy !== DEFAULT_FILTERS.sortBy,
    [draftFilters],
  );

  const loadReport = useCallback(
    (filters = DEFAULT_FILTERS) => {
      const code = AuthStorage.getItem("token");
      const loginEmail = AuthStorage.getItem("email");

      setIsLoading(true);
      setErrorMessage("");

      return ReportServices.getSubmittalsLastUpdatedTimeReport({
        company: company_id,
        project: id,
        code,
        loginEmail,
        startDate: filters.startDate || "",
        endDate: filters.endDate || "",
        sortBy: filters.sortBy || "",
      })
        .then((response) => {
          setRows(normalizeReportRows(response));
          setProjectName((currentName) =>
            currentName || response?.projectName || "",
          );
          setProjectAddress((currentAddress) =>
            currentAddress || response?.projectAddress || "",
          );
        })
        .catch((error) => {
          console.error("Failed to load submittals last updated report", error);
          setRows([]);
          setErrorMessage("Unable to load this report. Please try again.");
        })
        .finally(() => setIsLoading(false));
    },
    [company_id, id],
  );

  useEffect(() => {
    let isActive = true;
    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    setIsAccessLoading(true);
    ReportServices.getReportAccessLevel({
      company: company_id,
      project: id,
      code,
      loginEmail,
    })
      .then((response) => {
        if (!isActive) {
          return;
        }

        setPageAccessLevel(normalizeReportAccessLevel(response?.accessLevel));
        setCannedReportsGranularPermission(
          response?.cannedReportsGranularPermission || "",
        );
        setProjectName(response?.projectName || "");
        setProjectAddress(response?.projectAddress || "");
      })
      .catch((error) => {
        console.error("Failed to load report permissions", error);
        if (isActive) {
          setPageAccessLevel(0);
          setCannedReportsGranularPermission("");
        }
      })
      .finally(() => {
        if (isActive) {
          setIsAccessLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [company_id, id]);

  useEffect(() => {
    if (!isAccessLoading && canViewReport) {
      loadReport(DEFAULT_FILTERS);
    }
  }, [canViewReport, isAccessLoading, loadReport]);

  const handleApplyFilters = () => {
    if (!hasFilterChanges || !hasCompleteDateRange) {
      return;
    }

    setAppliedFilters(draftFilters);
    loadReport(draftFilters);
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSortBy(DEFAULT_FILTERS.sortBy);

    if (
      appliedFilters.startDate ||
      appliedFilters.endDate ||
      appliedFilters.sortBy !== DEFAULT_FILTERS.sortBy
    ) {
      setAppliedFilters(DEFAULT_FILTERS);
      loadReport(DEFAULT_FILTERS);
    }
  };

  const exportMenuItems = useMemo(
    () => [
      {
        key: "export-pdf",
        label: (
          <ExportReports
            data={rows}
            columns={columns}
            accessLevel={pageAccessLevel}
            editedData={[]}
            isLoading={isLoading}
            reportTitle={REPORT_TITLE}
            projectName={projectName}
            projAddress={projectAddress}
            company_id={company_id}
          />
        ),
      },
      {
        key: "export-csv",
        label: (
          <ExportReportsCSV
            data={rows}
            columns={columns}
            accessLevel={pageAccessLevel}
            editedData={[]}
            isLoading={isLoading}
            reportTitle={REPORT_TITLE}
            projectName={projectName}
            projAddress={projectAddress}
          />
        ),
      },
    ],
    [
      columns,
      company_id,
      isLoading,
      pageAccessLevel,
      projectAddress,
      projectName,
      rows,
    ],
  );

  return (
    <Container>
      {canViewReport && (
        <>
          <div className="row mt-4">
            <div className="col-md-6">
              <Text className={styles.header}>{REPORT_TITLE}</Text>
            </div>
            <div className="col-md-6 d-flex justify-content-sm-center justify-content-md-end gap-3">
              <div className="align-self-center d-flex gap-2">
                <SidebarDistribution
                  isLoading={isLoading}
                  reportName={DISTRIBUTION_REPORT_NAME}
                  reportOwner="Koncurent"
                />
                <Dropdown menu={{ items: exportMenuItems }}>
                  <Button>
                    Export <DownOutlined />
                  </Button>
                </Dropdown>
              </div>
            </div>
          </div>

          <div className={styles.filterBar}>
            <div className={styles.filterField}>
              <label>Start Date</label>
              <DatePicker
                value={startDate}
                format={DATE_FORMAT}
                placeholder={DATE_FORMAT}
                onChange={setStartDate}
              />
            </div>
            <div className={styles.filterField}>
              <label>End Date</label>
              <DatePicker
                value={endDate}
                format={DATE_FORMAT}
                placeholder={DATE_FORMAT}
                onChange={setEndDate}
              />
            </div>
            <div className={`${styles.filterField} ${styles.sortField}`}>
              <label>Sort By</label>
              <Select
                allowClear
                value={sortBy || undefined}
                placeholder="Default order"
                onChange={(value) => setSortBy(value || "")}
                options={[
                  { value: "Ascending", label: "Ascending" },
                  { value: "Descending", label: "Descending" },
                ]}
              />
            </div>
            <div className={styles.filterActions}>
              <Tooltip
                title={
                  !hasCompleteDateRange
                    ? "Select both a start date and an end date to apply the date filter."
                    : null
                }
              >
                <span className={styles.applyButtonWrapper}>
                  <Button
                    type="primary"
                    onClick={handleApplyFilters}
                    loading={isLoading}
                    disabled={!hasFilterChanges || !hasCompleteDateRange}
                  >
                    Apply Filters
                  </Button>
                </span>
              </Tooltip>
              {hasFiltersToClear && (
                <Button onClick={handleClearFilters} disabled={isLoading}>
                  Clear
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      <div className="row mt-4">
        {isAccessLoading || isLoading ? (
          <LoadingComponent />
        ) : !canViewReport ? (
          <div className="text-center">
            <Text>
              You do not have the necessary permissions to access this page.
            </Text>
          </div>
        ) : (
          <div className={styles.tableCard}>
            {errorMessage && (
              <Alert
                className={styles.errorMessage}
                type="error"
                message={errorMessage}
                showIcon
              />
            )}
            <Table
              columns={columns}
              dataSource={rows}
              bordered
              size="small"
              tableLayout="fixed"
              className={styles.reportTable}
              components={{
                header: { cell: ResizableHeaderCell },
              }}
              scroll={{
                x: Math.max(
                  900,
                  columns.reduce(
                    (totalWidth, column) =>
                      totalWidth + Number(column.width || 180),
                    0,
                  ),
                ),
                y: "68vh",
              }}
              pagination={{
                position: ["bottomCenter"],
                defaultPageSize: 20,
              }}
              locale={{
                emptyText: errorMessage
                  ? "No report data is available."
                  : "No submittals found for the selected filters.",
              }}
            />
          </div>
        )}
      </div>
    </Container>
  );
}

export default SubmittalsLastUpdatedTimeReport;
